import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/atoms/modal'
import { Button, ButtonType } from '@/components/atoms/button'

type BulkOperationModalProps = {
  title: string
  description: string
  /** フッター左に出す対象件数などの要約。件数が確定しない間は undefined */
  summary?: string
  submitLabel: string
  isSubmittable: boolean
  isSubmitting: boolean
  width?: number
  onCancel: () => void
  onSubmit: () => void
}

/**
 * 一括操作モーダルの外枠。ヘッダー/フッターと実行中の操作ロックを共通化する。
 */
export const BulkOperationModal: React.FC<BulkOperationModalProps> = ({
  title,
  description,
  summary,
  submitLabel,
  isSubmittable,
  isSubmitting,
  width = 620,
  onCancel,
  onSubmit,
  children,
}) => {
  const { t } = useTranslation()

  return (
    <Modal customCss={modalStyle(width)} isLoading={isSubmitting}>
      <ModalHeader customCss={modalHeaderStyle}>
        <div>{title}</div>
        <p className="description">{description}</p>
      </ModalHeader>
      <ModalBody customCss={modalBodyContainerStyle}>
        <div css={modalBodyStyle}>{children}</div>
      </ModalBody>
      <ModalFooter customCss={modalFooterStyle}>
        {summary && <div className="summary">{summary}</div>}
        {/* ButtonType.SecondaryDisabled は汎用の非活性スタイルではなく absolute 配置用のため使わない */}
        <Button
          buttonType={isSubmitting ? ButtonType.BorderDisabled : ButtonType.Secondary}
          height="38px"
          width="110px"
          marginRight={16}
          isDisabled={isSubmitting}
          onClick={onCancel}
        >
          {t('Cancel')}
        </Button>
        {/* 実行中のスピナーは Modal の isLoading に任せる。
            Button の loading はラベルを消したうえでオレンジの細線アニメを縮小表示するため視認できない */}
        <Button
          buttonType={isSubmittable ? ButtonType.Primary : ButtonType.PrimaryDisabled}
          height="38px"
          width="130px"
          isDisabled={!isSubmittable || isSubmitting}
          onClick={onSubmit}
        >
          {submitLabel}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

// margin: auto で中央寄せされるので left は指定しない。
// 背面のカレンダーを操作させないため、オーバーレイの pointer-events も既定のまま
const modalStyle = (width: number) => ({
  width,
  minWidth: width,
  // 内容量に合わせて伸縮させる。既定の固定高さだと本文にスクロールが出てしまう
  height: 'auto',
})

// 高さを内容に合わせる以上 ModalBody 側でスクロールさせる必要はない。
// 量が読めない部屋タイプ一覧だけは、モーダル側で個別に高さを持たせている
const modalBodyContainerStyle = css({
  overflow: 'visible',
  height: 'auto',
})

const modalHeaderStyle = css({
  padding: '22px 32px 18px',
  '.description': {
    fontSize: 12,
    fontWeight: 'normal',
    lineHeight: 1.6,
    color: '#a3a3a3',
    marginTop: 6,
  },
})

const modalBodyStyle = css({
  padding: 32,
  border: '1px solid #ddd',
  borderRadius: 8,
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
})

const modalFooterStyle = css({
  '.summary': {
    marginRight: 'auto',
    fontSize: 12,
    letterSpacing: 0.6,
    color: '#676767',
  },
})
